// ── DASH.B1 — Zone 1 KPI tile derivation ────────────────────────────────────
//
// Pure mapping from a tile id + the /dashboard/overview payload to the values
// a KpiTile renders. Kept separate from the component so it's unit-testable in
// isolation.
//
// Per design doc §11.4, Zone 1 tiles ALWAYS render (showing '0' / '—' when
// there is no data) — they never collapse. Tiles whose data source does not
// exist in the /overview payload yet (platform-wide counts, per-user "my work")
// return a neutral placeholder and are annotated below; they only surface for
// roles that are currently unreachable (platform_admin / developer fold), so
// the live tenant_admin / partner / client tiles are all backed by real data.

import type { KpiTileId } from '../../config/dashboardRoleConfig';
import type { ActiveProject, DashboardOverview, RagStatus, UpcomingGate } from '../../types/dashboard';
import { formatTimeAgo, phaseLabel, ragForDueDates } from '../../lib/dashboardFormat';

export interface KpiTileData {
  label: string;
  value: string | number;
  subLabel?: string;
  rag?: RagStatus;
  comingSoon?: boolean;
}

const TILE_LABELS: Record<KpiTileId, string> = {
  // Platform Admin
  active_tenants: 'Active tenants',
  active_projects_all: 'Active projects',
  red_projects_platform: 'At-risk projects',
  ai_cost_mtd_platform: 'AI cost (MTD)',
  open_critical_alerts: 'Critical alerts',
  // Tenant Admin / PM
  active_projects_by_phase: 'Active projects',
  sprint_progress: 'Sprint progress',
  open_risks: 'Open risks',
  ai_cost_mtd_tenant: 'AI cost (MTD)',
  phase_gates_week: 'Upcoming gates',
  // Developer
  current_sprint_day: 'Sprint day',
  my_stories: 'My stories',
  my_late_stories: 'My late stories',
  my_open_agent_sessions: 'Open agent sessions',
  // Partner
  tenants_under_partner: 'Client tenants',
  stale_artefacts: 'Stale artefacts',
  // Client
  active_project: 'Active project',
  last_activity: 'Last activity',
  // Shared
  pending_approvals: 'Pending approvals',
};

// Tiles whose value is "Coming soon" regardless of payload (design doc §5.2 —
// AI cost lands in Sprint 14).
const COMING_SOON_TILES = new Set<KpiTileId>(['ai_cost_mtd_tenant', 'ai_cost_mtd_platform']);

// Tiles with NO backing field in the /dashboard/overview payload — they would
// derive to a permanent '0'/'—' because the data source simply doesn't exist
// there. Zone 1 filters these out at render time (DASH.B1 FIX 3) so a LIVE role
// never sees a tile that can NEVER populate. This is distinct from a comingSoon
// tile (an intentional, labeled placeholder) and from a backed tile that
// legitimately shows '0' (real zero count, which §11.4 says to render).
//
// Partner's tenants_under_partner + stale_artefacts land here: /overview returns
// no partner rollup (effectiveRole is only PM|CLIENT). See #1310. The platform /
// developer "my work" tiles also land here, but those roles fold to Standard and
// never render live (see #1309) — listed for when they un-fold without data.
export const TILES_WITHOUT_OVERVIEW_DATA: ReadonlySet<KpiTileId> = new Set<KpiTileId>([
  'active_tenants',
  // Platform-wide counts: /overview is per-tenant only, so these derive from a
  // single tenant's data and would be mislabeled "platform-wide". Filtered for
  // the same reason as active_tenants (they share the platform-scope problem).
  'red_projects_platform',
  'open_critical_alerts',
  'tenants_under_partner',
  'stale_artefacts',
  'my_stories',
  'my_late_stories',
  'my_open_agent_sessions',
]);

function activeProjects(o: DashboardOverview): ActiveProject[] {
  return o.active_projects.data ?? [];
}
function gates(o: DashboardOverview): UpcomingGate[] {
  return o.upcoming_gates.data ?? [];
}
function atRiskCounts(projects: ActiveProject[]): { red: number; amber: number } {
  let red = 0;
  let amber = 0;
  for (const p of projects) {
    if (p.healthStatus === 'RED') red++;
    else if (p.healthStatus === 'AMBER') amber++;
  }
  return { red, amber };
}

/**
 * Derive the renderable data for a single Zone 1 tile. `now` is injectable for
 * deterministic tests of the relative-time / RAG-by-age tiles.
 */
export function getKpiTileData(
  tileId: KpiTileId,
  overview: DashboardOverview,
  now: number = Date.now(),
): KpiTileData {
  const label = TILE_LABELS[tileId];

  if (COMING_SOON_TILES.has(tileId)) {
    return { label, value: '—', comingSoon: true };
  }

  const projects = activeProjects(overview);

  switch (tileId) {
    // ── Tenant Admin / PM ──────────────────────────────────────────────────
    case 'active_projects_by_phase':
    case 'active_projects_all': {
      const phases = new Set(projects.map((p) => p.currentPhase));
      return {
        label,
        value: projects.length,
        subLabel: projects.length === 0 ? 'none active' : `across ${phases.size} phase${phases.size === 1 ? '' : 's'}`,
      };
    }
    case 'sprint_progress': {
      const done = projects.reduce((sum, p) => sum + (p.ptsDone ?? 0), 0);
      const remaining = projects.reduce((sum, p) => sum + (p.ptsRemaining ?? 0), 0);
      return { label, value: `${done} pts`, subLabel: `${remaining} pts remaining` };
    }
    case 'open_risks': {
      const { red, amber } = atRiskCounts(projects);
      return {
        label,
        value: red + amber,
        rag: red > 0 ? 'RED' : amber > 0 ? 'AMBER' : 'GREEN',
        subLabel: `${red} critical`,
      };
    }
    case 'phase_gates_week': {
      // Design doc §5.2 names this "phase gates passed this week"; the /overview
      // payload only exposes UPCOMING gates, so this is the closest backing
      // data (TODO DASH follow-on: a "gates passed" metric needs a new source).
      const g = gates(overview);
      return { label, value: g.length, subLabel: 'in the lookahead window' };
    }

    // ── Partner sponsor ────────────────────────────────────────────────────
    case 'pending_approvals': {
      const g = gates(overview);
      return { label, value: g.length, rag: ragForDueDates(g, now), subLabel: g.length === 0 ? 'all clear' : 'awaiting you' };
    }
    case 'tenants_under_partner':
      // No per-partner tenant rollup in /overview (partner aggregation is a
      // separate surface). Neutral placeholder until that source is wired.
      return { label, value: '—', subLabel: 'partner view' };
    case 'stale_artefacts':
      // No artefact-staleness data in /overview yet (B2 / future source). '—'
      // (not 0) — consistent with the other unbacked tiles; this id is filtered
      // out of Zone 1 anyway (TILES_WITHOUT_OVERVIEW_DATA).
      return { label, value: '—', subLabel: 'awaiting sign-off' };

    // ── Client sponsor ─────────────────────────────────────────────────────
    case 'active_project': {
      const first = projects[0];
      return first
        ? { label, value: first.name, subLabel: phaseLabel(first.currentPhase) }
        : { label, value: 'No project', subLabel: '—' };
    }
    case 'last_activity': {
      const first = projects[0];
      return first
        ? { label, value: formatTimeAgo(first.lastActivityAt, now) }
        : { label, value: '—' };
    }

    // ── Platform Admin (currently unreachable — folds to tenant_admin) ──────
    case 'red_projects_platform': {
      const { red } = atRiskCounts(projects);
      return { label, value: red, rag: red > 0 ? 'RED' : 'GREEN', subLabel: 'platform-wide' };
    }
    case 'active_tenants':
    case 'open_critical_alerts':
      // No platform-wide rollup in /overview — '—' placeholder; both ids are
      // filtered out of Zone 1 (TILES_WITHOUT_OVERVIEW_DATA).
      return { label, value: '—', subLabel: 'platform-wide' };

    // ── Developer (currently unreachable — folds to tenant_admin) ──────────
    case 'current_sprint_day': {
      const first = projects[0];
      return first ? { label, value: first.sprintDay, subLabel: `of ${first.sprintTotal}` } : { label, value: 0 };
    }
    case 'my_stories':
    case 'my_late_stories':
    case 'my_open_agent_sessions':
      // No per-user "my work" data in /overview (needs a project-role-scoped
      // source). Neutral placeholder; tile only shows for the developer fold.
      return { label, value: '—', subLabel: 'my work' };

    default:
      return { label: label ?? tileId, value: '—' };
  }
}
